import { el } from "./dom.js";

/**
 * The look of the two screens a fan sees before the venue: the loading curtain
 * and the gate.
 *
 * ## Why they share a module
 *
 * They were two different dark rectangles. The loading screen was a cold
 * blue-black field with an emoji on it; the gate was a card floating on a
 * slightly different cold blue-black field. Nothing carried from one to the
 * next, so arriving read as a sequence of unrelated screens rather than as one
 * approach to a place.
 *
 * Both now draw from here, which means the lamp a fan watches while the venue
 * loads is the same lamp still burning on the card when it asks their name,
 * and the light behind it never changes. That continuity is most of what makes
 * an arrival feel deliberate.
 *
 * ## Why so little of it
 *
 * There is one light, one flame and a handful of embers, and that is the whole
 * inventory. The temptation on a title screen is to keep adding — a skyline, a
 * border, a second glow — and every one of those costs the flame the attention
 * it needs to be the thing you look at. A vigil is a dark room with one light
 * in it. Drawing more than that is drawing something else.
 */

/** Embers rising through the frame. Few, because a shower of sparks is a fire. */
const EMBER_COUNT = 14;

/** Deterministic 0..1, so the drift is the same on every reload. */
function wobble(seed: number): number {
  const s = Math.sin(seed * 12.9898) * 43758.5453;
  return s - Math.floor(s);
}

/**
 * The backdrop: night above, a warm pool below, and embers going up through it.
 *
 * The light is placed BELOW the bottom edge rather than behind the middle, so
 * it reads as a lamp standing just out of shot rather than as a glow painted
 * on the wall. Everything on top of it is lit from underneath, which is how a
 * face over a diya is lit and the reason the screen feels like a vigil rather
 * than a menu.
 */
export function vigilBackdrop(): HTMLDivElement {
  const embers = el("div", { className: "zg-embers", attrs: { "aria-hidden": "true" } });

  for (let i = 0; i < EMBER_COUNT; i += 1) {
    const ember = el("div", { className: "zg-ember" });
    // Spread across the width, but pulled toward the middle where the light
    // is: embers coming up out of the dark corners have nothing to rise from.
    const across = 8 + wobble(i * 3 + 1) * 84;
    ember.style.left = `${across.toFixed(1)}%`;
    ember.style.setProperty("--drift", `${(wobble(i * 7 + 2) * 60 - 30).toFixed(0)}px`);
    ember.style.setProperty("--size", `${(1.4 + wobble(i * 11 + 3) * 1.6).toFixed(1)}px`);
    // Long and unequal. Embers falling into step is the tell that they are a
    // loop, and at these durations one pass is slower than most fans will wait.
    ember.style.animationDuration = `${(11 + wobble(i * 13 + 4) * 12).toFixed(1)}s`;
    ember.style.animationDelay = `${(-wobble(i * 17 + 5) * 20).toFixed(1)}s`;
    embers.appendChild(ember);
  }

  return el("div", {
    className: "zg-vigil",
    attrs: { "aria-hidden": "true" },
    children: [embers],
  });
}

/**
 * A diya: a clay bowl with a flame standing in it.
 *
 * Drawn rather than set as 🪔, because the emoji is whatever the device
 * decided a lamp looks like — on Windows it is an orange disc with a tick
 * beside it, which is what was on the loading screen. This one is three
 * rounded boxes and a shadow, it is the same on every device, and its glow is
 * the actual light source the rest of the screen is graded against.
 */
export function diyaLamp(): HTMLDivElement {
  return el("div", {
    className: "zg-diya",
    attrs: { "aria-hidden": "true" },
    children: [
      el("span", { className: "zg-diya-glow" }),
      el("span", { className: "zg-diya-flame" }),
      el("span", { className: "zg-diya-bowl" }),
    ],
  });
}
